<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import {
    SIDE_PANEL_RESHAPE_BUFFER_MS,
    SIDE_PANEL_RESHAPE_DURATION_MS,
    SIDE_PANEL_WIDTH_PX
  } from '@/constants/sidePanel'

  /**
   * Generic right-edge sidebar shell.
   *
   * Renders a fixed panel that slides in from the right and sits below the global Django header
   * (rendered outside the Vue app — see base.html's `<header class="navbar">`). The header height
   * is measured at runtime so the panel sticks flush against the header bottom regardless of
   * how the navbar is styled. Falls back to `topOffset` if the navbar can't be found.
   *
   * Layout pushing is the caller's responsibility: the app shell shrinks its content area by
   * reading the same `modelValue` from the store. This component only owns its own visual shell,
   * not the surrounding layout.
   */
  const props = withDefaults(
    defineProps<{
      modelValue: boolean
      /** Fallback header offset in px when the navbar element can't be measured. */
      topOffset?: number
      /** When true, render as a full-viewport overlay instead of the right-edge side drawer. */
      overlay?: boolean
    }>(),
    {
      topOffset: 0,
      overlay: false
    }
  )

  const measuredTop = ref<number>(props.topOffset)
  let navbarEl: HTMLElement | null = null

  /**
   * Transient flag toggled on for the duration of a docked ↔ overlay reshape so the
   * scoped ``.side-panel-shell--reshaping`` class can attach a CSS transition for
   * exactly that motion. Outside of the reshape window the shell has no transition
   * declared, so any value change driven by ``--scrollbar-width`` flipping (radix-vue
   * scroll-lock activating / releasing when a Dialog opens / closes) lands in a single
   * frame instead of animating from old to new — which is what was producing the
   * "panel slides 15px sideways and back" flicker on classic-scrollbar OSes.
   */
  const isReshaping = ref(false)
  let reshapeTimer: ReturnType<typeof setTimeout> | null = null

  /**
   * Locate the primary app navbar. When an impersonation banner is present, base.html renders
   * two `<header class="navbar">` elements — the impersonation strip first, then the real nav.
   * We target the one containing `#navbar-menu` to always get the correct header.
   */
  function findNavbar(): HTMLElement | null {
    return (
      document.querySelector<HTMLElement>('#navbar-menu')?.closest<HTMLElement>('header.navbar') ??
      null
    )
  }

  /**
   * Recomputed on mount, resize, and scroll. The Django navbar is `position: relative`, so as the
   * page scrolls down its bottom edge moves up and eventually goes negative. Clamping to 0 keeps
   * the fixed panel flush against the viewport top once the header has scrolled out of view,
   * instead of leaving a gap that reveals the scrolling content behind.
   */
  const measure = () => {
    if (!navbarEl) {
      navbarEl = findNavbar()
    }
    if (navbarEl) {
      measuredTop.value = Math.max(0, navbarEl.getBoundingClientRect().bottom)
    }
  }

  function addListeners() {
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, { passive: true, capture: true })
  }

  function removeListeners() {
    window.removeEventListener('resize', measure)
    window.removeEventListener('scroll', measure, { capture: true } as EventListenerOptions)
  }

  onMounted(() => {
    navbarEl = findNavbar()
    measure()
    if (props.modelValue) addListeners()
  })

  watch(
    () => props.modelValue,
    (open) => {
      if (open) {
        measure()
        addListeners()
      } else {
        removeListeners()
      }
    }
  )

  /**
   * Arm the reshape transition window when the parent flips ``overlay``. The class is
   * cleared a bit after the CSS transition ends — long enough for the browser to paint
   * the final position before the transition declaration is dropped, otherwise the panel
   * would snap to its final geometry instead of finishing the slide.
   *
   * Both halves of the timing (the CSS ``transition: left 300ms`` declaration in the
   * scoped style block below and the JS clear timer here) are derived from
   * :const:`SIDE_PANEL_RESHAPE_DURATION_MS` so the two sides cannot drift if the value is
   * tuned. The CSS side cannot interpolate the JS constant directly inside a Vue SFC
   * scoped style block, so the style declaration carries a matching comment that points
   * at the constant; CI / code review is the only enforcement that catches a one-sided
   * change.
   */
  watch(
    () => props.overlay,
    () => {
      if (reshapeTimer !== null) {
        clearTimeout(reshapeTimer)
      }
      isReshaping.value = true
      reshapeTimer = setTimeout(() => {
        isReshaping.value = false
        reshapeTimer = null
      }, SIDE_PANEL_RESHAPE_DURATION_MS + SIDE_PANEL_RESHAPE_BUFFER_MS)
    }
  )

  onBeforeUnmount(() => {
    removeListeners()
    if (reshapeTimer !== null) {
      clearTimeout(reshapeTimer)
      reshapeTimer = null
    }
  })

  /**
   * Inline style for the panel shell — covers both docked and overlay modes with the same
   * set of CSS properties so the browser can transition smoothly between them.
   *
   * Why a unified style instead of two class lists with ``inset-0`` vs ``w-[400px]``:
   * CSS animations only interpolate values for the **same** property; switching between
   * ``width: 400px`` (Tailwind class) and ``left: 0; right: 0`` (no width) gives the
   * browser nothing to interpolate, so the layout snaps instantly. By always pinning all
   * four edges (``top``, ``right``, ``bottom``, ``left``) and only changing the values,
   * the ``transition`` declaration on the ``<aside>`` produces a continuous slide between
   * docked → overlay → docked.
   *
   * Per-property semantics:
   *
   * - ``top``: tracks the Django navbar's bottom edge in both modes — overlay must never
   *   paint over the navbar (was the bug behind the previous ``inset-0`` overlay).
   * - ``right``: ``0`` in both modes. There is *no* ``var(--scrollbar-width)`` compensation
   *   here because :file:`assets/index.css` promotes ``<html>`` to ``overflow-y: scroll``
   *   on classic-scrollbar OSes — that anchors the scrollbar to html, blocks ``body
   *   { overflow: hidden }`` from propagating to the viewport when a Dialog opens, and so
   *   the layout viewport width never actually changes. radix-vue still writes
   *   ``--scrollbar-width: 15px`` on body when scroll-lock activates (it has no way to
   *   know we sidestepped the propagation), so consuming that variable here would
   *   *over*-shift the panel 15px to the left every time a modal opens.
   * - ``bottom``: 0 in both modes.
   * - ``left``: the differing axis — docked sits ``SIDE_PANEL_WIDTH_PX`` from the right
   *   (computed via ``calc`` so the browser can interpolate to ``0`` when going to
   *   overlay), overlay flush against the left viewport edge.
   */
  const panelStyle = computed(() => {
    const top = `${measuredTop.value}px`
    if (props.overlay) {
      return { top, right: '0', bottom: '0', left: '0' }
    }
    return {
      top,
      right: '0',
      bottom: '0',
      left: `calc(100% - ${SIDE_PANEL_WIDTH_PX}px)`
    }
  })
</script>

<template>
  <!--
    Three orthogonal transitions:

    1. The outer ``<Transition>`` owns enter / leave (mount / unmount of the panel)
       and binds the ``.side-panel-enter-active`` / ``.side-panel-leave-active``
       classes that declare the ``transform`` + ``left`` slide.
    2. ``.side-panel-shell--reshaping`` (toggled by the ``isReshaping`` flag from the
       ``watch(() => props.overlay)`` watcher) declares a 300ms transition on
       ``left`` + ``border-left-color`` for the docked ↔ overlay reshape. It is only
       attached during the 320ms reshape window, never persistently.
    3. Outside of those two windows the shell has no transition declared, which is
       intentional: it makes any value change driven by radix-vue scroll-lock
       (``--scrollbar-width`` flipping when a Dialog opens / closes) land in a single
       frame instead of animating. ``right`` is also kept out of every transition list
       for the same reason — see the scoped style block below.
  -->
  <Transition
    enter-active-class="side-panel-enter-active"
    enter-from-class="translate-x-full"
    enter-to-class="translate-x-0"
    leave-active-class="side-panel-leave-active"
    leave-from-class="translate-x-0"
    leave-to-class="translate-x-full"
  >
    <aside
      v-if="props.modelValue"
      :class="[
        // Shared shell — same in both modes so the browser only interpolates the values
        // that actually differ (the inline-style ``left`` axis and the border-left color).
        'side-panel-shell fixed z-[60] flex flex-col overscroll-contain border-l bg-white',
        // Docked-only: clip overflow so the chat surface doesn't bleed past the 400px
        // boundary mid-transition. Overlay drops it because the panel is edge-to-edge,
        // and ``overflow: hidden`` would clip floating popovers (model selector, etc.).
        props.overlay ? 'border-l-transparent' : 'overflow-hidden border-gray-200',
        // Attach the reshape transition only while ``overlay`` is changing, so radix-vue
        // scroll-lock toggling ``--scrollbar-width`` mid-session can't animate the panel
        // sideways and back in response to a single-frame layout settle.
        isReshaping ? 'side-panel-shell--reshaping' : null
      ]"
      :style="panelStyle"
      role="complementary"
    >
      <slot />
    </aside>
  </Transition>
</template>

<style scoped>
  /*
   * Reshape transition (docked ↔ overlay) — gated on the ``--reshaping`` modifier.
   *
   * The ``<Transition>`` wrapper above handles enter / leave (mount / unmount). When the
   * panel stays mounted but its geometry changes (docked collapses into overlay,
   * overlay shrinks back to docked), the browser needs an explicit ``transition``
   * declaration to interpolate the inline-style ``left`` value and the class-driven
   * ``border-left-color``.
   *
   * Crucially, this transition is attached **only** while ``isReshaping`` is true (a
   * window armed by the ``watch(() => props.overlay)`` in the script and held open for
   * :const:`SIDE_PANEL_RESHAPE_DURATION_MS` plus a small paint buffer). Outside of that
   * window the shell has no transition declared, which keeps any layout settle —
   * navbar height changes, parent padding-right toggling on side-panel open/close, etc —
   * from accidentally animating the panel sideways. With an always-on transition,
   * browsers occasionally split related style writes across paint frames; ``left``'s
   * computed value momentarily differs by a few pixels and the transition cheerfully
   * animates the panel sideways and back over 300ms (≈1s of perceived motion), which
   * reads as an unpolished flicker.
   *
   * Property timings mirror the slide-in motion:
   * - ``left``: 300ms ease-out — same curve as the enter animation, so reshaping reads
   *   as a continuation of the original slide-in. ``left`` is the only positional axis
   *   that differs between docked and overlay. The 300ms here MUST stay in lockstep with
   *   :const:`SIDE_PANEL_RESHAPE_DURATION_MS` (defined in :file:`constants/sidePanel.ts`)
   *   so the JS arming window does not clip the animation early or hold the transition
   *   declaration long after it has finished. Vue SFC scoped styles cannot interpolate
   *   the JS constant, so this duration is updated by hand if the constant changes.
   * - ``border-left-color``: 200ms ease-out — fades the left border out as the panel
   *   reaches the viewport's left edge in overlay mode, instead of snapping to
   *   transparent at frame 0.
   *
   * ``right`` is not listed — it holds the same ``0`` value in both modes, nothing to
   * interpolate. ``top`` is also omitted — it tracks the navbar's measured bottom edge
   * on every scroll/resize event, so animating it would lag the panel behind the user's
   * scroll.
   */
  /*
   * All transition declarations are gated on ``prefers-reduced-motion: no-preference`` so
   * users who have opted into reduced motion (system-level "Reduce Motion" on macOS / iOS,
   * "Show animations" off on Windows / GNOME, vestibular-sensitivity preferences, etc.)
   * get instant snaps instead of the 300ms slide on every docked ↔ overlay reshape and
   * mount / unmount. Inverting the media query to gate the *presence* of the rule (rather
   * than overriding ``transition: none !important`` inside ``prefers-reduced-motion: reduce``)
   * keeps the cascade simple — no rule is declared in the reduced-motion case at all, so
   * there is nothing for a future override to fight against.
   *
   * The ``isReshaping`` arming in the script is intentionally NOT gated on the same media
   * query. The class is still toggled for its 300ms-ish window, but with the transition
   * declaration removed under reduced motion the class becomes a harmless no-op — and
   * keeping the JS branch unconditional means tests can probe ``isReshaping`` flipping
   * regardless of the JSDOM ``matchMedia`` stub fidelity.
   */
  @media (prefers-reduced-motion: no-preference) {
    .side-panel-shell--reshaping {
      transition:
        left 300ms ease-out,
        border-left-color 200ms ease-out;
    }

    /*
     * Enter / leave overrides for the ``<Transition>`` wrapper.
     *
     * Mounting / unmounting translates the panel (slide in from the right / slide out to
     * the right), so the enter / leave classes declare both ``transform`` and ``left``.
     * ``left`` matters here because :func:`closeSidePanel` resets ``chatMode`` to
     * ``'sidepanel'`` synchronously with ``isSidePanelOpen=false``, so closing from
     * overlay would otherwise snap the panel to docked-shape (left jumps from ``0`` to
     * ``calc(100% - 400px)``) for one frame before sliding off. Including ``left`` in the
     * leave transition makes the panel shrink and slide off as one continuous motion.
     *
     * ``right`` is intentionally omitted: it holds the same ``0`` value in docked and
     * overlay, so it has nothing meaningful to interpolate, and keeping it out of the
     * transition list shields the panel from any spurious sideways animation triggered by
     * unrelated layout settles during enter / leave.
     */
    .side-panel-enter-active {
      transition:
        transform 300ms ease-out,
        left 300ms ease-out;
    }

    .side-panel-leave-active {
      transition:
        transform 200ms ease-in,
        left 200ms ease-in,
        border-left-color 200ms ease-in;
    }
  }
</style>
